import { useEffect, useRef, useState } from 'react'

/** The return type for the {@link useDebouncedSearch} hook. */
type UseDebouncedSearchReturn = {
  inputValue: string
  debouncedValue: string
  setInputValue: (value: string) => void
}

/**
 * A custom hook for implementing debounced search functionality. It manages an input
 * value and a debounced value that updates after a specified delay.
 *
 * @param delay The debounce delay in milliseconds (default is 300ms).
 * @returns An object containing the input value, debounced value, and a function to
 *   update the input value @see {@link UseDebouncedSearchReturn}.
 */
export function useDebouncedSearch(delay = 300): UseDebouncedSearchReturn {
  const [inputValue, setInputValue] = useState('')
  const [debouncedValue, setDebouncedValue] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const previousInputRef = useRef(inputValue)

  useEffect(() => {
    if (inputValue === previousInputRef.current) return
    previousInputRef.current = inputValue
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedValue(inputValue)
    }, delay)
    return () => clearTimeout(debounceRef.current)
  }, [inputValue, delay])

  return { inputValue, debouncedValue, setInputValue }
}
